import React, { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { useApolloClient, useMutation, useQuery } from '@apollo/client';
import { format, formatDistanceToNow } from 'date-fns';
import { useAccount } from '../../context/AccountContext';
import {
  GET_TINYCMS_FORM,
  GET_TINYCMS_FORM_RESPONSES,
  GET_TINYCMS_FORM_SETTINGS,
  GET_TINYCMS_FORMS,
  GET_TINYCMS_UNREAD_RESPONSES,
} from '../../graphql';
import {
  DELETE_TINYCMS_FORM_RESPONSE,
  MARK_TINYCMS_FORM_RESPONSES_READ,
} from '../../graphql/mutations';
import { pageStatus } from './CmsPages';
import {
  type FormField,
  type FormRow,
  type ResponseRow,
  placedLabel,
  receivedLabel,
  responsesCsv,
} from './formResponses';
import { ApprovalHistory, Finding, FindingsList, PublishPanel } from './shared';
import { Loader } from '../common/PageLoader';

/**
 * Content → Forms ("TinyCMS UX optimization review v7", Prototype v3): the
 * site's forms in a rail; the one picked as a read-only preview (Form) or
 * what readers sent through it (Responses), with the way to publish it when
 * the agent has drafted or changed it.
 *
 * The agent builds forms (MCP create_form / update_form) and places them on
 * pages; a member publishes them here. Responses are user-generated content
 * kept for the owner — readable only here (never emailed, never over MCP),
 * kept as long as Form settings says.
 */

const PAGE_SIZE = 50;
const plural = (n: number, word: string) => `${n} ${word}${n === 1 ? '' : 's'}`;

export function CmsFormsView({ initial }: { initial?: string | null }) {
  const { accountId } = useAccount();
  const { data, loading, refetch } = useQuery(GET_TINYCMS_FORMS, {
    variables: { accountId },
    skip: !accountId,
    fetchPolicy: 'cache-and-network',
  });
  const forms: FormRow[] = useMemo(() => data?.tinycmsForms || [], [data]);
  const [sel, setSel] = useState<string | null>(initial || null);

  if (loading && !forms.length) return <Loader className="ink-muted" style={{ marginTop: 28 }} />;
  if (!forms.length) {
    return (
      <div className="pg-empty">
        <div className="pg-empty-title">No forms yet</div>
        <p>
          Ask your agent for one: “Add a contact form to the About page”, or “Put a newsletter
          sign-up on the home page”. It appears here for you to publish, and what readers send shows
          up here too.
        </p>
      </div>
    );
  }

  const form = forms.find((f) => f.id === sel) || forms[0];
  return (
    <div className="pg">
      <aside className="pg-rail">
        <div>
          <div className="pg-rail-head">FORMS</div>
          {forms.map((f) => {
            const st = pageStatus(f.state);
            return (
              <button
                key={f.id}
                type="button"
                className="pg-rail-item"
                data-active={form.id === f.id ? 'true' : undefined}
                onClick={() => setSel(f.id)}
              >
                <span className="pg-dot" style={{ background: st.dot }} />
                <span className="pg-rail-text">
                  <span className="pg-rail-title">{f.name}</span>
                  <span className="pg-rail-sub">{placedLabel(f.placedOn)}</span>
                </span>
                <span
                  className="fm-count"
                  data-unread={f.responses.unread ? 'true' : undefined}
                  title={
                    f.responses.unread
                      ? `${plural(f.responses.unread, 'new response')}`
                      : plural(f.responses.total, 'response')
                  }
                >
                  {f.responses.unread ? `${f.responses.unread} new` : f.responses.total}
                </span>
              </button>
            );
          })}
        </div>
      </aside>
      <div className="pg-main">
        <FormDetail key={form.id} row={form} onChanged={() => void refetch()} />
      </div>
    </div>
  );
}

function FormDetail({ row, onChanged }: { row: FormRow; onChanged: () => void }) {
  const [tab, setTab] = useState<'form' | 'responses'>(row.responses.unread ? 'responses' : 'form');
  const [checksOpen, setChecksOpen] = useState(false);
  const st = pageStatus(row.state);
  const live = Boolean(row.state.live);
  const waiting = !live || row.state.hasUnpublishedChanges;

  const openChecks = () => {
    setChecksOpen(true);
    setTimeout(
      () => document.getElementById('fm-checks')?.scrollIntoView({ behavior: 'smooth' }),
      0
    );
  };

  return (
    <>
      <div className="pg-head">
        <div style={{ minWidth: 0 }}>
          <div className="pg-title">{row.name}</div>
          <div className="pg-meta">
            <span className="pg-status" style={{ color: st.color }}>
              <span className="pg-dot" style={{ background: st.dot }} />
              {st.label}
            </span>
            <span>·</span>
            {row.placedOn.length ? (
              <span>
                On{' '}
                {row.placedOn.map((p, i) => (
                  <React.Fragment key={p.id}>
                    {i ? ', ' : ''}
                    <Link to={`/content?view=pages&page=${p.id}`} className="pg-url">
                      {p.title}
                    </Link>
                  </React.Fragment>
                ))}
              </span>
            ) : (
              <span className="pg-url" data-muted="true">
                Not on a page yet
              </span>
            )}
            <span>·</span>
            <span>
              {plural(row.fieldCount, 'field')}
              {row.updatedAt
                ? ` · updated ${formatDistanceToNow(new Date(row.updatedAt), { addSuffix: true })}`
                : ''}
            </span>
          </div>
        </div>
        <div className="pg-seg" role="tablist" aria-label="Form or responses">
          <button
            type="button"
            role="tab"
            aria-selected={tab === 'form'}
            data-active={tab === 'form' ? 'true' : undefined}
            onClick={() => setTab('form')}
          >
            Form
          </button>
          <button
            type="button"
            role="tab"
            aria-selected={tab === 'responses'}
            data-active={tab === 'responses' ? 'true' : undefined}
            onClick={() => setTab('responses')}
          >
            Responses · {row.responses.total}
          </button>
        </div>
      </div>

      {waiting ? (
        <div className="pg-action">
          <span
            className="pg-dot"
            style={{ background: 'var(--ink-amber)', width: 8, height: 8 }}
          />
          <div className="pg-action-text">
            <div className="pg-action-title">
              {live ? 'Your agent changed this form' : 'Your agent drafted this form'}
            </div>
            <div className="pg-action-note">
              {live
                ? 'Readers still see the published version until you publish.'
                : 'Readers can’t see it yet. Publish it with the page it’s on.'}
            </div>
          </div>
          <button type="button" className="ink-btn ink-btn-primary" onClick={openChecks}>
            Review and publish
          </button>
        </div>
      ) : null}

      {tab === 'form' ? (
        <FormPreview id={row.id} />
      ) : (
        <ResponsesPane row={row} onChanged={onChanged} />
      )}

      <div className="pg-more">
        <button
          type="button"
          className="pg-more-toggle"
          aria-expanded={checksOpen}
          onClick={() => setChecksOpen((v) => !v)}
        >
          {checksOpen ? '▾' : '▸'} Checks, publishing and history
        </button>
        {checksOpen ? <FormChecks id={row.id} onChanged={onChanged} /> : null}
      </div>
    </>
  );
}

/** The working copy as readers will see it, in the app's own styles; read-only. */
function FormPreview({ id }: { id: string }) {
  const { accountId } = useAccount();
  const { data, loading } = useQuery(GET_TINYCMS_FORM, {
    variables: { accountId, id },
    skip: !accountId,
    fetchPolicy: 'cache-and-network',
  });
  const { data: settingsData } = useQuery(GET_TINYCMS_FORM_SETTINGS, {
    variables: { accountId },
    skip: !accountId,
    fetchPolicy: 'cache-first',
  });
  const doc = data?.tinycmsForm?.content;
  const consent: string | null = settingsData?.tinycmsFormSettings?.consent || null;
  if (loading && !doc) return <Loader label="Loading" className="ink-muted" />;
  if (!doc) return null;
  const inline = doc.layout === 'inline';
  return (
    <>
      <div className="fm-preview" data-layout={inline ? 'inline' : 'stacked'}>
        {doc.heading ? <div className="fm-preview-heading">{doc.heading}</div> : null}
        {doc.intro ? <div className="fm-preview-intro">{doc.intro}</div> : null}
        <div className="fm-preview-fields">
          {(doc.fields as FormField[]).map((f) => (
            <div key={f.name} className="fm-preview-field" data-type={f.type}>
              {f.type === 'checkbox' ? (
                <span className="fm-preview-check">
                  <span className="pg-box" /> {f.label}
                </span>
              ) : (
                <>
                  {!inline ? (
                    <span className="fm-preview-label">
                      {f.label}
                      {f.required ? ' *' : ''}
                    </span>
                  ) : null}
                  <span
                    className="fm-preview-input"
                    data-tall={f.type === 'textarea' ? 'true' : undefined}
                  >
                    {f.type === 'select'
                      ? `${f.placeholder || 'Choose one'} ▾`
                      : f.placeholder || (inline ? f.label : '')}
                  </span>
                </>
              )}
            </div>
          ))}
          <span className="fm-preview-button">{doc.submitLabel || 'Send'}</span>
        </div>
        {consent ? <div className="fm-preview-consent">{consent}</div> : null}
      </div>
      <p className="pg-note">
        Read-only preview. To change fields or wording, ask your agent.
        {doc.successPath ? ` After sending, readers go to ${doc.successPath}.` : ''}
      </p>
    </>
  );
}

function ResponsesPane({ row, onChanged }: { row: FormRow; onChanged: () => void }) {
  const { accountId } = useAccount();
  const apollo = useApolloClient();
  const [limit, setLimit] = useState(PAGE_SIZE);
  const [open, setOpen] = useState<string | null>(null);
  const [exporting, setExporting] = useState(false);
  const [error, setError] = useState('');
  const { data, loading, refetch } = useQuery(GET_TINYCMS_FORM_RESPONSES, {
    variables: { accountId, formId: row.id, limit, offset: 0 },
    skip: !accountId,
    fetchPolicy: 'cache-and-network',
  });
  const { data: formData } = useQuery(GET_TINYCMS_FORM, {
    variables: { accountId, id: row.id },
    skip: !accountId,
    fetchPolicy: 'cache-first',
  });
  const [markRead] = useMutation(MARK_TINYCMS_FORM_RESPONSES_READ);
  const [remove, { loading: removing }] = useMutation(DELETE_TINYCMS_FORM_RESPONSE);
  const fields: FormField[] = formData?.tinycmsForm?.content?.fields || [];
  const result = data?.tinycmsFormResponses;
  const responses: ResponseRow[] = result?.responses || [];
  const total: number = result?.total ?? row.responses.total;
  const unread: number = result?.unread ?? row.responses.unread;

  // Two columns of the form after Received: its first fields a person fills in.
  const shown = fields.filter((f) => f.type !== 'checkbox').slice(0, 2);
  const grid = {
    gridTemplateColumns: `130px ${shown.map(() => 'minmax(0, 1fr)').join(' ')} 110px`,
  };

  const changed = async () => {
    await refetch();
    onChanged();
    // The Content badge and the Forms tab count.
    void apollo.refetchQueries({ include: [GET_TINYCMS_UNREAD_RESPONSES, GET_TINYCMS_FORMS] });
  };

  const toggle = async (r: ResponseRow) => {
    setOpen((cur) => (cur === r.id ? null : r.id));
    if (!r.read) {
      await markRead({ variables: { accountId, formId: row.id, ids: [r.id] } });
      await changed();
    }
  };

  const markAll = async () => {
    await markRead({ variables: { accountId, formId: row.id } });
    await changed();
  };

  const del = async (r: ResponseRow) => {
    if (!window.confirm('Delete this response for good? It can’t be brought back.')) return;
    setError('');
    try {
      await remove({ variables: { accountId, id: r.id } });
      setOpen(null);
      await changed();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Couldn’t delete the response');
    }
  };

  const exportCsv = async () => {
    setExporting(true);
    setError('');
    try {
      const all: ResponseRow[] = [];
      for (let offset = 0; offset < total; offset += 1000) {
        const { data: page } = await apollo.query({
          query: GET_TINYCMS_FORM_RESPONSES,
          variables: { accountId, formId: row.id, limit: 1000, offset },
          fetchPolicy: 'network-only',
        });
        const got: ResponseRow[] = page?.tinycmsFormResponses?.responses || [];
        all.push(...got);
        if (got.length < 1000) break;
      }
      const blob = new Blob([responsesCsv(fields, all)], { type: 'text/csv;charset=utf-8' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${row.name.replace(/[^\w-]+/g, '-').toLowerCase() || 'form'}-responses.csv`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Couldn’t export the responses');
    } finally {
      setExporting(false);
    }
  };

  const cellFor = (r: ResponseRow, f: FormField) =>
    r.fields.find((v) => v.name === f.name)?.value || '';

  return (
    <div className="fm-responses">
      <div className="fm-resp-bar">
        <span className="fm-resp-total">
          {plural(total, 'response')}
          {unread ? <span className="fm-resp-new"> · {unread} new</span> : null}
        </span>
        {total > responses.length && responses.length ? (
          <span className="ink-muted">Showing the latest {responses.length}</span>
        ) : null}
        <span style={{ marginLeft: 'auto', display: 'flex', gap: 8 }}>
          {unread ? (
            <button type="button" className="ink-btn ink-btn-quiet" onClick={() => void markAll()}>
              Mark all read
            </button>
          ) : null}
          <button
            type="button"
            className="ink-btn ink-btn-quiet"
            disabled={!total || exporting}
            onClick={() => void exportCsv()}
          >
            {exporting ? 'Exporting…' : 'Export CSV'}
          </button>
        </span>
      </div>
      {error ? <p className="text-xs text-error">{error}</p> : null}

      {loading && !responses.length ? (
        <Loader label="Loading" className="ink-muted" />
      ) : !responses.length ? (
        <p className="fm-resp-empty">
          {row.state.live
            ? row.placedOn.length
              ? 'No responses yet.'
              : 'No responses yet. Ask your agent to put this form on a page.'
            : 'No responses yet. Readers can send this form once it’s published.'}
        </p>
      ) : (
        <div className="fm-table" role="table" aria-label="Responses">
          <div className="fm-row fm-row-head" role="row" style={grid}>
            <span role="columnheader">Received</span>
            {shown.map((f) => (
              <span key={f.name} role="columnheader">
                {f.label}
              </span>
            ))}
            <span role="columnheader">Page</span>
          </div>
          {responses.map((r) => (
            <React.Fragment key={r.id}>
              <button
                type="button"
                className="fm-row"
                role="row"
                style={grid}
                data-unread={r.read ? undefined : 'true'}
                aria-expanded={open === r.id}
                onClick={() => void toggle(r)}
              >
                <span role="cell">
                  {!r.read ? <span className="fm-unread-dot" aria-label="New" /> : null}
                  {receivedLabel(r.receivedAt)}
                </span>
                {shown.map((f) => (
                  <span key={f.name} role="cell" className="fm-cell">
                    {cellFor(r, f)}
                  </span>
                ))}
                <span role="cell" className="ink-muted">
                  {r.page || ''}
                </span>
              </button>
              {open === r.id ? (
                <div className="fm-detail">
                  <dl>
                    {r.fields.map((v) => (
                      <React.Fragment key={v.name}>
                        <dt>{v.label}</dt>
                        <dd>{v.value}</dd>
                      </React.Fragment>
                    ))}
                    <dt>Received</dt>
                    <dd>{format(new Date(r.receivedAt), 'MMM d, yyyy h:mm a')}</dd>
                  </dl>
                  <button
                    type="button"
                    className="ink-btn ink-btn-quiet"
                    disabled={removing}
                    onClick={() => void del(r)}
                  >
                    Delete response
                  </button>
                </div>
              ) : null}
            </React.Fragment>
          ))}
          {total > responses.length ? (
            <button
              type="button"
              className="ink-btn ink-btn-quiet"
              style={{ marginTop: 12 }}
              onClick={() => setLimit((n) => n + PAGE_SIZE)}
            >
              Show {Math.min(PAGE_SIZE, total - responses.length)} more
            </button>
          ) : null}
        </div>
      )}
    </div>
  );
}

/** A form's checks, publish controls and approval history. */
function FormChecks({ id, onChanged }: { id: string; onChanged: () => void }) {
  const { accountId } = useAccount();
  const { data, loading, refetch } = useQuery(GET_TINYCMS_FORM, {
    variables: { accountId, id },
    skip: !accountId,
    fetchPolicy: 'network-only',
  });
  const form = data?.tinycmsForm;
  if (loading && !form) return <Loader label="Loading" className="ink-site-row-desc" />;
  if (!form) return null;
  const findings: Finding[] = form.state.preflight || [];
  const blocking = findings.filter((f) => f.severity === 'blocking').length;
  return (
    <div id="fm-checks" className="pg-checks">
      <div>
        <p className="ink-site-kicker">Checks</p>
        <FindingsList findings={findings} empty="No findings." />
      </div>
      <div>
        <p className="ink-site-kicker">Publish</p>
        <PublishPanel
          kind="form"
          id={id}
          workingHash={form.workingHash}
          state={form.state}
          blocking={blocking}
          onDone={() => {
            void refetch();
            onChanged();
          }}
        />
      </div>
      <div>
        <p className="ink-site-kicker">History</p>
        <ApprovalHistory kind="form" subjectId={id} limit={10} />
      </div>
    </div>
  );
}
